import {objectFromEntries} from '@benchling/js-utils';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetResultBlobs,
  GetResultBlobsVariables,
} from 'insights/analytics-visualizations/blobs/__generated/GetResultBlobs';
import {BLOB_QUERY} from 'insights/analytics-visualizations/blobs/QueryingBlobLinkList';
import {BlobLinkPreviewTabConsumer} from 'notebook/data-tables/BlobLinkPreviewTabContext';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {BlobUploadStatus} from 'schemas/tags/BlobConstants';
import Tooltip, {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ResultBlobGraphQL} from '../../../graphql-fragments/results/__generated/ResultBlobGraphQL';
import * as BlobLinkTab from './BlobLinkTab';

type ResultBlobNode = GetResultBlobs['resultBlobs'][number];

interface PreviewButtonWrapperProps {
  renderButton: (fileId: string) => JSX.Element;
}

function PreviewButtonWrapper(props: PreviewButtonWrapperProps): JSX.Element {
  return (
    <BlobLinkPreviewTabConsumer>
      {({fileId}) =>
        fileId ? (
          <Tooltip title="Preview" delay={{show: 500}}>
            {props.renderButton(fileId)}
          </Tooltip>
        ) : null
      }
    </BlobLinkPreviewTabConsumer>
  );
}

function DownloadButtonWrapper({children}: {children: JSX.Element}): JSX.Element {
  return (
    <Tooltip title="Download" delay={{show: 500}}>
      {children}
    </Tooltip>
  );
}

interface BlobLinkCollapsiblePreviewProps extends ResultBlobGraphQL {
  initiallyExpanded?: boolean;
  chipSize?: ChipSize;
  shouldPreviewImages?: boolean;
  owningEntityApiId: string;
}

export function BlobLinkCollapsiblePreview({
  __typename,
  id,
  name,
  previewFileJson,
  pdfPreviewFileJson,
  uploadStatus,
  url,
  owningEntityApiId,
  initiallyExpanded = false,
  chipSize = ChipSize.SMALL,
  shouldPreviewImages = true,
}: BlobLinkCollapsiblePreviewProps): JSX.Element {
  const blob: ResultBlobGraphQL = {
    __typename,
    id,
    name,
    previewFileJson,
    pdfPreviewFileJson,
    uploadStatus,
    url,
  };

  if (shouldPreviewImages && previewFileJson?.previewType === EXTERNAL_FILE_TYPE.IMAGE) {
    return (
      <CollapsibleCard
        leftHeader={name}
        initiallyExpanded={initiallyExpanded}
        rightHeader={
          <>
            <PreviewButtonWrapper
              renderButton={(fileId: string) => (
                <a
                  className="u-mini-spacing-right"
                  onClick={(e) => {
                    e.stopPropagation();
                    BlobLinkTab.tabState.open(fileId, blob, owningEntityApiId);
                  }}
                >
                  <BenchlingIcon icon="open" />
                </a>
              )}
            />
            <DownloadButtonWrapper>
              <a
                href={url}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <BenchlingIcon icon="download" />
              </a>
            </DownloadButtonWrapper>
          </>
        }
      >
        <SignedExtFileURLProxy
          url={previewFileJson.url}
          externalFileId={previewFileJson.id}
          renderWithSignedURL={renderWithSignedUrl}
          owningEntityApiId={owningEntityApiId}
        />
      </CollapsibleCard>
    );
  }
  return <Chip className="chip-container" size={chipSize} text={name} href={url} />;
}

interface BlobLinkListProps {
  blobs: Array<ResultBlobGraphQL>;
  blobClassName?: string;
  chipSize?: ChipSize;
  shouldPreviewImages?: boolean;
  shouldPreviewPDFs?: boolean;
  owningEntityApiId?: string;
}

export function CollapsibleBlobLinkList({
  blobs,
  chipSize = ChipSize.SMALL,
  owningEntityApiId,
  shouldPreviewImages = true,
}: BlobLinkListProps): JSX.Element | null {
  if (blobs.length === 0) {
    return null;
  }

  const blobLinks = blobs.map((blob, index) => (
    <BlobLinkCollapsiblePreview
      key={index}
      {...blob}
      chipSize={chipSize}
      initiallyExpanded={blobs.length === 1}
      shouldPreviewImages={shouldPreviewImages}
      // TODO[BNCH-78973]: Need to make this field not optional
      owningEntityApiId={owningEntityApiId ?? ''}
    />
  ));
  return <div>{blobLinks}</div>;
}

interface BlobLinkProps extends ResultBlobGraphQL {
  className?: string;
  chipSize?: ChipSize;
  shouldPreviewImages?: boolean;
  shouldPreviewPDFs?: boolean;
  owningEntityApiId: string;
}

/*
 * Shows an image if the blob has a previewFile that is an image. Otherwise shows a chip.
 * If configs are enabled for result blobs show preview button for PDF preview of chips.
 */
function _BlobLink(props: BlobLinkProps): JSX.Element {
  if (props.shouldPreviewImages && props.previewFileJson?.previewType === EXTERNAL_FILE_TYPE.IMAGE) {
    return (
      <div className={classNames(props.className, 'blobLinkPreview')}>
        <SignedExtFileURLProxy
          url={props.previewFileJson.url}
          externalFileId={props.previewFileJson.id}
          renderWithSignedURL={renderWithSignedUrl}
          owningEntityApiId={props.owningEntityApiId}
        />
        <div className="blobLinkPreview-bar">
          {props.shouldPreviewPDFs && !props.pdfPreviewFileJson && (
            <TooltipWrapper tooltipTitle="PDF preview missing">
              <BenchlingIcon icon="status-warning" color="#fc9f35" style={{marginRight: '3px'}} />
            </TooltipWrapper>
          )}
          <div className="blobLinkPreview-filename">{props.name}</div>
          <PreviewButtonWrapper
            renderButton={(fileId: string) => (
              <ButtonLink
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                onClick={() => {
                  BlobLinkTab.tabState.open(fileId, props, props.owningEntityApiId);
                }}
                icon="open"
                size={ButtonSize.MINI}
                className="blobLinkPreview-button"
                disabled={props.pdfPreviewFileJson == null && props.previewFileJson == null}
              />
            )}
          />
          <DownloadButtonWrapper>
            <ButtonLink
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              href={props.url}
              icon="download"
              size={ButtonSize.MINI}
              className="blobLinkPreview-button"
            />
          </DownloadButtonWrapper>
        </div>
      </div>
    );
  }
  return (
    <div className={classNames(props.className, 'blobLinkPreview')}>
      <Chip
        className={
          props.shouldPreviewPDFs && !props.pdfPreviewFileJson
            ? 'chip-container chip--warningLighter'
            : 'chip-container'
        }
        size={props.chipSize!}
        text={props.name}
        href={props.url}
        isReadonly={true}
        tooltipTitle={props.shouldPreviewPDFs && !props.pdfPreviewFileJson ? 'PDF preview missing' : ''}
      />
      <div className="blobLinkChipPreview-bar">
        <div className="fadeBar">
          {props.shouldPreviewPDFs && (
            <PreviewButtonWrapper
              renderButton={(fileId: string) => (
                <ButtonLink
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  onClick={() => {
                    BlobLinkTab.tabState.open(fileId, props, props.owningEntityApiId);
                  }}
                  icon="open"
                  size={ButtonSize.MINI}
                  className="blobLinkPreview-button u-display-inline-block"
                  disabled={props.pdfPreviewFileJson == null}
                />
              )}
            />
          )}
          <DownloadButtonWrapper>
            <ButtonLink
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              href={props.url}
              icon="download"
              size={ButtonSize.MINI}
              className="blobLinkPreview-button"
            />
          </DownloadButtonWrapper>
        </div>
      </div>
    </div>
  );
}

_BlobLink.defaultProps = {
  chipSize: ChipSize.SMALL,
  shouldPreviewImages: true,
};

const BlobLink = React.memo(_BlobLink);
const REFETCH_STATUS_INTERVAL_MS = 5000; // 5 seconds
const PDF_PREVIEW_POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

/**
 * Polls for blob upload completion and PDF preview availability.
 *
 * Phase 1: Refetches until all blobs are COMPLETE or ABORTED.
 * Phase 2: Once uploads are done, continues polling for PDF previews
 * (if shouldPreviewPDFs is enabled) up to PDF_PREVIEW_POLL_TIMEOUT_MS.
 */
function useBlobStatusPolling(
  blobStatus: string,
  blobStatusResult: SkippableBenchlingQueryResult<GetResultBlobs, GetResultBlobsVariables>,
  shouldPreviewPDFs: boolean | undefined,
  blobCount: number
): void {
  const pdfPreviewPollStartRef = useRef<number | null>(null);
  const prevBlobCountRef = useRef(blobCount);

  useEffect(() => {
    // When new blobs are added, reset the timeout so the latest upload
    // gets a full polling window for its preview to arrive.
    if (blobCount > prevBlobCountRef.current) {
      pdfPreviewPollStartRef.current = null;
    }
    prevBlobCountRef.current = blobCount;

    const intervalId = setInterval(() => {
      // Phase 1: Wait for all blob uploads to finish.
      if (blobStatus !== BlobUploadStatus.COMPLETE) {
        blobStatusResult.refetch();
        return;
      }

      // Phase 2: Once uploads are complete, check if any blobs are still
      // waiting for their PDF preview to be generated.
      const anyPendingPreviews =
        shouldPreviewPDFs === true &&
        blobStatusResult.type === QueryResultType.SUCCESS &&
        blobStatusResult.data.resultBlobs.some(
          (blob) =>
            !isInaccessible(blob) &&
            blob.uploadStatus === BlobUploadStatus.COMPLETE &&
            !blob.pdfPreviewFileJson
        );

      if (!anyPendingPreviews) {
        pdfPreviewPollStartRef.current = null;
        clearInterval(intervalId);
        return;
      }

      // Start the timeout clock the first time we see pending previews.
      if (pdfPreviewPollStartRef.current == null) {
        pdfPreviewPollStartRef.current = Date.now();
      }

      // Keep polling until the timeout elapses, then stop.
      if (Date.now() - pdfPreviewPollStartRef.current < PDF_PREVIEW_POLL_TIMEOUT_MS) {
        blobStatusResult.refetch();
      } else {
        clearInterval(intervalId);
      }
    }, REFETCH_STATUS_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [blobStatus, blobStatusResult, shouldPreviewPDFs, blobCount]);
}

/*
 * Shows a vertical list of BlobLinks for each blob.
 */
// TODO (mayank): Make list scrollable if too many blobs
export const BlobLinkList = ({
  blobs,
  blobClassName,
  chipSize = ChipSize.SMALL,
  owningEntityApiId,
  shouldPreviewImages = true,
  shouldPreviewPDFs,
}: BlobLinkListProps): JSX.Element | null => {
  const [blobStatus, setBlobStatus] = useState<string>(BlobUploadStatus.IN_PROGRESS);
  const [liveBlobsById, setLiveBlobsById] = useState<{[id: string]: ResultBlobNode}>({});
  const blobStatusResult = useSkippableBenchlingQuery<GetResultBlobs, GetResultBlobsVariables>(BLOB_QUERY, {
    variables: {
      ids: blobs.map((blob) => blob.id),
    },
  });

  // We only set the blobStatus to COMPLETE when all result blobs are either complete or aborted
  // Otherwise the blobStatus remains IN_PROGRESS
  if (
    blobStatusResult.type === QueryResultType.SUCCESS &&
    blobStatusResult.data.resultBlobs.every(
      (blob) =>
        isInaccessible(blob) ||
        blob.uploadStatus === BlobUploadStatus.COMPLETE ||
        blob.uploadStatus === BlobUploadStatus.ABORTED
    )
  ) {
    if (blobStatus === BlobUploadStatus.IN_PROGRESS) {
      setBlobStatus(BlobUploadStatus.COMPLETE);
    }
    const newLiveBlobsById = objectFromEntries(
      blobStatusResult.data.resultBlobs.map((blob): [string, ResultBlobNode] =>
        isInaccessible(blob) ? [blob.invalidId, blob] : [blob.id, blob]
      )
    );
    if (!isEqual(liveBlobsById, newLiveBlobsById)) {
      setLiveBlobsById(newLiveBlobsById);
    }
  }

  useBlobStatusPolling(blobStatus, blobStatusResult, shouldPreviewPDFs, blobs.length);

  if (blobs.length === 0) {
    return null;
  }

  const blobLinks = blobs.map((blob, index) => {
    const liveBlob = liveBlobsById[blob.id];
    if (blobStatus !== BlobUploadStatus.COMPLETE || !liveBlob) {
      return (
        <p className="blobLinkPreview-image" key={index} {...loadingIndicatorAttrs()}>
          Loading...
        </p>
      );
    }
    if (isInaccessible(liveBlob)) {
      return <NoAccessChip size={chipSize} key={liveBlob.invalidId} />;
    }
    if (liveBlob.uploadStatus === BlobUploadStatus.ABORTED) {
      return (
        <p className="blobLinkPreview-image" key={index}>
          Error loading file
        </p>
      );
    }

    return (
      <BlobLink
        className={blobClassName}
        key={index}
        {...liveBlob}
        chipSize={chipSize}
        shouldPreviewImages={shouldPreviewImages}
        shouldPreviewPDFs={shouldPreviewPDFs}
        owningEntityApiId={owningEntityApiId ?? ''}
      />
    );
  });

  return <div>{blobLinks}</div>;
};

export function BlobLinkListInTable(props: Omit<BlobLinkListProps, 'shouldPreviewPDFs'>): JSX.Element {
  const resultBlobConfig =
    BenchlingConfig.ELN_EXPORT_PDF_CONFIG.attachResultBlobsInline &&
    BenchlingConfig.ELN_EXPORT_PDF_CONFIG.includeFileAttachmentsInline;
  return <BlobLinkList {...props} shouldPreviewPDFs={resultBlobConfig} />;
}

function renderWithSignedUrl(signedURL: string): JSX.Element {
  if (signedURL === undefined) {
    return (
      <p className="blobLinkPreview-image" {...loadingIndicatorAttrs()}>
        Loading...
      </p>
    );
  }
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (signedURL === null) {
    return <p className="blobLinkPreview-image">Error loading file</p>;
  }
  return <img src={signedURL} alt="blob preview" className="blobLinkPreview-image" />;
}
